import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { App, Button, Drawer, Form, Input, InputNumber, Select, Space, Switch } from 'antd';
import { useState } from 'react';
import { saveCategory } from '../api';
import {
  CategoryInput,
  CategoryStat,
  GROUP_PRESETS,
  GalaCategory,
  MEDAL_KEYS,
  MEDAL_LABEL,
  MedalKey,
  errorText,
  isMedalKey,
} from '../contract';
import medalCyan from '../medals/medal-cyan.svg';
import medalGold from '../medals/medal-gold.svg';
import medalPink from '../medals/medal-pink.svg';
import medalRed from '../medals/medal-red.svg';
import medalRose from '../medals/medal-rose.svg';
import styles from '../styles.module.scss';

const { TextArea } = Input;

/** Huy hiệu SVG xem trước (chép từ GALA-2026/assets). Khoá khác 5 màu này là ảnh tuỳ chỉnh, không xem trước được. */
export const MEDAL_SRC: Record<MedalKey, string> = {
  pink: medalPink,
  rose: medalRose,
  gold: medalGold,
  cyan: medalCyan,
  red: medalRed,
};

/** Ảnh huy hiệu nhỏ; khoá lạ -> hiện khung trống để bảng không vỡ. */
export const MedalImg = ({ medal }: { medal: string }) =>
  isMedalKey(medal) ? (
    <img className={styles.medalImg} src={MEDAL_SRC[medal]} alt={`Huy hiệu ${MEDAL_LABEL[medal]}`} />
  ) : (
    <span className={styles.medalImg} aria-hidden />
  );

/** Ô chọn một trong 5 huy hiệu (dùng như control của Form.Item). */
const MedalPicker = ({ value, onChange }: { value?: string; onChange?: (v: string) => void }) => (
  <div>
    <div className={styles.medalPicker} role="radiogroup" aria-label="Huy hiệu">
      {MEDAL_KEYS.map((key) => (
        <button
          key={key}
          type="button"
          role="radio"
          aria-checked={value === key}
          className={`${styles.medalOption} ${value === key ? styles.medalOptionOn : ''}`}
          onClick={() => onChange?.(key)}
        >
          <img src={MEDAL_SRC[key]} alt="" />
          {MEDAL_LABEL[key]}
        </button>
      ))}
    </div>
    {value && !isMedalKey(value) && (
      <p className={styles.hint}>
        Hạng mục này đang dùng huy hiệu tuỳ chỉnh ({value}). Chọn một màu ở trên để đổi.
      </p>
    )}
  </div>
);

interface FormValues {
  code: string;
  nameEn: string;
  nameVi: string;
  groupCode: string;
  groupLabel: string;
  medal: string;
  stats: Partial<CategoryStat>[];
  conditions: string[];
  quantityCount: number | null;
  quantityLabel: string;
  isHidden: boolean;
}

const clean = (v?: string | null) => (v ?? '').trim();

function toFormValues(c: GalaCategory | null): FormValues {
  return {
    code: c?.code ?? '',
    nameEn: c?.nameEn ?? '',
    nameVi: c?.nameVi ?? '',
    groupCode: c?.groupCode ?? GROUP_PRESETS[0].code,
    groupLabel: c?.groupLabel ?? GROUP_PRESETS[0].label,
    medal: c?.medal ?? MEDAL_KEYS[0],
    stats: (c?.stats ?? []).map((s) => ({ ...s })),
    conditions: [...(c?.conditions ?? [])],
    quantityCount: c?.quantityCount ?? null,
    quantityLabel: c?.quantityLabel ?? '',
    isHidden: c?.isHidden ?? false,
  };
}

interface Props {
  open: boolean;
  eventId: number;
  /** Hạng mục đang sửa; null = thêm mới. */
  category: GalaCategory | null;
  /** Mã gợi ý cho hạng mục mới (số kế tiếp, 2 chữ số). */
  suggestCode: string;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

/** Ngăn kéo thêm / sửa một hạng mục giải (chọn huy hiệu có xem trước). */
const CategoryDrawer = ({ open, eventId, category, suggestCode, onClose, onSaved }: Props) => {
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);

  const initial = toFormValues(category);
  if (!category) initial.code = suggestCode;

  const groupOptions = [...GROUP_PRESETS];
  if (category && !groupOptions.some((g) => g.code === category.groupCode)) {
    groupOptions.push({ code: category.groupCode, label: category.groupLabel });
  }

  const onOk = async () => {
    const v = await form.validateFields();
    const input: CategoryInput = {
      code: clean(v.code),
      nameEn: clean(v.nameEn),
      nameVi: clean(v.nameVi),
      groupCode: v.groupCode,
      groupLabel: clean(v.groupLabel),
      medal: v.medal,
      stats: (v.stats ?? []).slice(0, 3).map((s) => ({
        icon: clean(s.icon),
        value: clean(s.value),
        label: clean(s.label),
      })),
      conditions: (v.conditions ?? []).map(clean).filter(Boolean),
      quantityCount: typeof v.quantityCount === 'number' ? v.quantityCount : null,
      quantityLabel: clean(v.quantityLabel),
      isHidden: v.isHidden,
    };
    setSaving(true);
    try {
      await saveCategory(eventId, input, category?.id);
      message.success(category ? 'Đã lưu hạng mục.' : 'Đã thêm hạng mục.');
      await onSaved();
      onClose();
    } catch (e) {
      message.error(
        errorText(e, 'Không lưu được hạng mục. Mã hạng mục không được trùng với hạng mục khác.')
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={680}
      title={category ? `Sửa hạng mục ${category.code}` : 'Thêm hạng mục'}
      destroyOnHidden
      maskClosable={false}
      // Form dựng lại mỗi lần mở; reset thêm sau khi mở để chắc chắn lấy giá trị của hạng mục đang chọn.
      afterOpenChange={(opened) => opened && form.resetFields()}
      extra={
        <Space>
          <Button onClick={onClose}>Huỷ</Button>
          <Button type="primary" loading={saving} onClick={onOk}>
            Lưu
          </Button>
        </Space>
      }
    >
      <Form<FormValues>
        form={form}
        layout="vertical"
        initialValues={initial}
        preserve={false}
        requiredMark="optional"
      >
        <div className={styles.row2}>
          <Form.Item
            name="code"
            label="Mã hạng mục"
            extra="Ví dụ 01, 02... Không trùng trong cùng sự kiện."
            rules={[
              { required: true, whitespace: true, message: 'Nhập mã hạng mục.' },
              { max: 8, message: 'Không quá 8 ký tự.' },
            ]}
          >
            <Input style={{ maxWidth: 140 }} />
          </Form.Item>
          <Form.Item name="isHidden" label="Ẩn hạng mục" valuePropName="checked" extra="Ẩn thì app không hiện.">
            <Switch />
          </Form.Item>
        </div>
        <Form.Item
          name="nameVi"
          label="Tên tiếng Việt"
          rules={[
            { required: true, whitespace: true, message: 'Nhập tên tiếng Việt.' },
            { max: 200, message: 'Không quá 200 ký tự.' },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="nameEn"
          label="Tên tiếng Anh"
          rules={[
            { required: true, whitespace: true, message: 'Nhập tên tiếng Anh.' },
            { max: 200, message: 'Không quá 200 ký tự.' },
          ]}
        >
          <Input placeholder="[Top Creators of the Year 2026]" />
        </Form.Item>

        <div className={styles.row2}>
          <Form.Item name="groupCode" label="Nhóm lọc trong app" rules={[{ required: true }]}>
            <Select
              options={groupOptions.map((g) => ({ value: g.code, label: g.label }))}
              onChange={(code: string) => {
                const preset = GROUP_PRESETS.find((g) => g.code === code);
                if (preset && !clean(form.getFieldValue('groupLabel'))) {
                  form.setFieldsValue({ groupLabel: preset.label });
                }
              }}
            />
          </Form.Item>
          <Form.Item
            name="groupLabel"
            label="Chữ hiện trên thẻ"
            rules={[
              { required: true, whitespace: true, message: 'Nhập chữ nhóm.' },
              { max: 80, message: 'Không quá 80 ký tự.' },
            ]}
          >
            <Input />
          </Form.Item>
        </div>

        <Form.Item name="medal" label="Huy hiệu" rules={[{ required: true, message: 'Chọn huy hiệu.' }]}>
          <MedalPicker />
        </Form.Item>

        <Form.Item label="Ô chỉ số (tối đa 3)" required={false}>
          <Form.List name="stats">
            {(fields, { add, remove, move }) => (
              <>
                {fields.map((field, index) => (
                  <div key={field.key} className={styles.statRow}>
                    <Form.Item name={[field.name, 'icon']} style={{ marginBottom: 0 }}>
                      <Input placeholder="💎" aria-label="Biểu tượng" />
                    </Form.Item>
                    <Form.Item name={[field.name, 'value']} style={{ marginBottom: 0 }}>
                      <Input placeholder="10M KC" aria-label="Giá trị" />
                    </Form.Item>
                    <Space.Compact style={{ width: '100%' }}>
                      <Form.Item name={[field.name, 'label']} noStyle>
                        <Input placeholder="Mốc doanh thu" aria-label="Nhãn" />
                      </Form.Item>
                      <Button
                        icon={<ArrowUpOutlined />}
                        aria-label="Dời lên"
                        disabled={index === 0}
                        onClick={() => move(index, index - 1)}
                      />
                      <Button
                        icon={<ArrowDownOutlined />}
                        aria-label="Dời xuống"
                        disabled={index === fields.length - 1}
                        onClick={() => move(index, index + 1)}
                      />
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        aria-label="Xoá ô"
                        onClick={() => remove(field.name)}
                      />
                    </Space.Compact>
                  </div>
                ))}
                <Button icon={<PlusOutlined />} disabled={fields.length >= 3} onClick={() => add({})}>
                  Thêm ô chỉ số
                </Button>
              </>
            )}
          </Form.List>
        </Form.Item>

        <Form.Item label="Điều kiện" required={false} extra={'Mỗi dòng một điều kiện; bọc **chữ** để in đậm.'}>
          <Form.List name="conditions">
            {(fields, { add, remove, move }) => (
              <>
                {fields.map((field, index) => (
                  <div key={field.key} className={styles.subItem} style={{ marginBottom: 8 }}>
                    <div className={styles.subItemTools}>
                      <Button
                        size="small"
                        icon={<ArrowUpOutlined />}
                        aria-label="Dời lên"
                        disabled={index === 0}
                        onClick={() => move(index, index - 1)}
                      />
                      <Button
                        size="small"
                        icon={<ArrowDownOutlined />}
                        aria-label="Dời xuống"
                        disabled={index === fields.length - 1}
                        onClick={() => move(index, index + 1)}
                      />
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        aria-label="Xoá điều kiện"
                        onClick={() => remove(field.name)}
                      />
                    </div>
                    <Form.Item name={field.name} style={{ marginBottom: 0 }}>
                      <TextArea autoSize={{ minRows: 2, maxRows: 6 }} />
                    </Form.Item>
                  </div>
                ))}
                <Button icon={<PlusOutlined />} onClick={() => add('')}>
                  Thêm điều kiện
                </Button>
              </>
            )}
          </Form.List>
        </Form.Item>

        <div className={styles.row2}>
          <Form.Item name="quantityCount" label="Số lượng giải">
            <InputNumber min={0} precision={0} style={{ width: '100%' }} placeholder="3" />
          </Form.Item>
          <Form.Item
            name="quantityLabel"
            label="Chữ số lượng"
            rules={[{ max: 120, message: 'Không quá 120 ký tự.' }]}
          >
            <Input placeholder="3 Creator" />
          </Form.Item>
        </div>
      </Form>
    </Drawer>
  );
};

export default CategoryDrawer;
